import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { StatsCard } from '@/components/shared/StatsCard';
import { ChartPlaceholder } from '@/components/shared/ChartPlaceholder';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Button } from '@/components/ui/button';
import { Users, FileBarChart, DollarSign, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { agencyAPI } from '@/services/api';
import { toast } from 'sonner';

export default function AgencyDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    total_users: 0,
    total_filings: 0,
    revenue: 0,
    growth: '+0%'
  });
  const [recentFilings, setRecentFilings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch agency statistics
        const statsData = await agencyAPI.getAgencyStats();
        setStats(statsData);
        
        // Fetch recent filings
        const filingsData = await agencyAPI.getRecentFilings(3);
        setRecentFilings(filingsData);
        
      } catch (error) {
        console.error('Error fetching agency data:', error);
        toast.error('Failed to load agency dashboard data');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  if (!user) {
    return <div>Loading...</div>;
  }

  if (loading) {
    return (
      <DashboardLayout role={user.role} title="Agency Dashboard" user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <DashboardLayout role={user.role} title="Agency Dashboard" user={user}>
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold text-foreground">Agency Dashboard</h2>
          <p className="mt-1 text-muted-foreground">White-label platform overview</p>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <StatsCard
            title="Total Users"
            value={stats.total_users.toString()}
            icon={Users}
            iconBgColor="bg-primary"
          />
          <StatsCard
            title="Total Filings"
            value={stats.total_filings.toString()}
            icon={FileBarChart}
            iconBgColor="bg-secondary"
          />
          <StatsCard
            title="Revenue"
            value={`$${stats.revenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            icon={DollarSign}
            iconBgColor="bg-success"
          />
          <StatsCard
            title="Growth"
            value={stats.growth}
            icon={TrendingUp}
            iconBgColor="bg-info"
          />
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Recent Filings */}
          <Card className="card-elevated">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Recent Filings</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/agency/filings')}
                >
                  View All
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentFilings.length > 0 ? (
                  recentFilings.map((filing) => (
                    <div
                      key={filing.id}
                      className="flex items-center justify-between rounded-lg border p-3"
                    >
                      <div>
                        <p className="font-medium text-foreground">{filing.user}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(filing.date)}</p>
                      </div>
                      <StatusBadge status={filing.status} />
                    </div>
                  ))
                ) : (
                  <p className="text-center text-muted-foreground py-4">No recent filings</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                className="w-full justify-start"
                variant="outline"
                onClick={() => navigate('/agency/theme')}
              >
                Customize Brand Theme
              </Button>
              <Button
                className="w-full justify-start"
                variant="outline"
                onClick={() => navigate('/agency/users')}
              >
                Manage Users
              </Button>
              <Button
                className="w-full justify-start"
                variant="outline"
                onClick={() => navigate('/agency/settings')}
              >
                Platform Settings
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid gap-6 lg:grid-cols-2">
          <ChartPlaceholder title="User Growth" height={300} />
          <ChartPlaceholder title="Revenue Trend" height={300} />
        </div>
      </div>
    </DashboardLayout>
  );
}
